// src/components/Dashboard.js

import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useTheme } from '../../context/ThemeContext';

ChartJS.register(ArcElement, Tooltip, Legend);

export const Dashboard = ({ files }) => {
  const { theme } = useTheme();

  const categoryCounts = useMemo(() => {
    const counts = {};
    files.forEach(file => {
      const category = file.category || "Other";
      counts[category] = (counts[category] || 0) + 1;
    });
    return counts;
  }, [files]);

  const chartData = {
    labels: Object.keys(categoryCounts),
    datasets: [
      {
        label: 'No of Files',
        data: Object.values(categoryCounts),
        backgroundColor: [
          '#6366f1', // Indigo 500
          '#818cf8', // Indigo 400
          '#a5b4fc', // Indigo 300
          '#4f46e5', // Indigo 600
          '#c7d2fe', // Indigo 200
          '#3730a3'  // Indigo 800
        ],
        borderColor: theme === 'dark' ? '#09090b' : '#f7f4eb',
        borderWidth: 2.5,
        hoverOffset: 6
      },
    ],
  };
  
  const textColor = theme === 'dark' ? '#e4e4e7' : '#5e5752';

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { 
          color: textColor,
          font: {
            family: 'Inter',
            size: 11,
            weight: '500'
          },
          boxWidth: 8,
          boxHeight: 8,
          padding: 12
        }
      },
      tooltip: {
        backgroundColor: theme === 'dark' ? '#18181b' : '#faf8f5',
        titleColor: theme === 'dark' ? '#fafafa' : '#2b2522',
        bodyColor: theme === 'dark' ? '#e4e4e7' : '#5e5752',
        borderColor: theme === 'dark' ? 'rgba(63,63,70,0.5)' : '#e6dfd3',
        borderWidth: 1,
        padding: 10,
        boxWidth: 6,
        boxHeight: 6,
        usePointStyle: true
      },
      title: { display: false }
    }
  };

  const totalSize = useMemo(() => {
    return files.reduce((acc, file) => acc + (file.size || 0), 0);
  }, [files]);
  
  const maxQuota = 1 * 1024 * 1024 * 1024; // 1GB free tier cap
  const usagePercentage = Math.min((totalSize / maxQuota) * 100, 100);

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  if (files.length === 0 || Object.keys(categoryCounts).length === 0) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-metrics-card telemetry-offline">
          <div className="card-header-telemetry">
            <h4 className="telemetry-title">DashBoard</h4>
          </div>
          <div className="offline-telemetry-body">
            <div className="radar-grid-offline"></div>
            <span className="telemetry-status-text">SYSTEM STANDBY</span>
            <p className="telemetry-desc">Upload documents to initialize AI semantic metrics.</p>
          </div>
        </div>
        
        <div className="storage-quota-card">
          <div className="card-header-telemetry">
            <h4 className="telemetry-title">Storage</h4>
          </div>
          <div className="storage-header">
            <span className="storage-info">
              <strong>0 Bytes</strong> of 1 GB (0.00%)
            </span>
          </div>
          <div className="storage-bar-track">
            <div className="storage-bar-fill" style={{ width: '0%' }} />
          </div>
          <span className="storage-caption">Free Trial subscription allocation</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-metrics-card">
        <div className="card-header-telemetry">
          <h4 className="telemetry-title">DashBoard</h4>
        </div>
        <div className="chart-wrapper">
          <Doughnut options={chartOptions} data={chartData} />
        </div>
      </div>
      
      <div className="storage-quota-card">
        <div className="card-header-telemetry">
          <h4 className="telemetry-title">Storage</h4>
        </div>
        <div className="storage-header">
          <span className="storage-info">
            <strong>{formatSize(totalSize)}</strong> of 1 GB ({usagePercentage.toFixed(2)}%)
          </span>
        </div>
        <div className="storage-bar-track">
          <div className="storage-bar-fill" style={{ width: `${usagePercentage}%` }} />
        </div>
        <span className="storage-caption">Free Trial subscription allocation</span>
      </div>
    </div>
  );
};